import { AxiosResponse } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';
import { IForgotPasswordForm, ILoginForm, IRegisterForm, IResetPasswordForm } from './auth.types';
import { ApiRoutesConfig, RoutesConfig } from '../../../config';
import { RootStore } from '../../../stores/store';
import { LocalStorageService } from '../../../services';
import debounce from 'lodash.debounce';

const apiRoutes = new ApiRoutesConfig();
const routes = new RoutesConfig();

class AuthStore {
  public rootStore: RootStore;
  public isEmailConfirmInit = false;
  public loginIsValid: boolean | undefined;
  public referrerLogin: string | undefined;
  private _resetToken: string | undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, undefined, { autoBind: true });
    this._loadFromLocalStorage();
  }

  public dispose() {
    this.isEmailConfirmInit = false;
  }

  public async register(values: IRegisterForm) {
    const { email, password, confirmPassword, remember, login } = values;
    if (email && password && login && password === confirmPassword && remember) {
      await this.rootStore.request({
        method: 'post',
        route: apiRoutes.registration,
        params: {
          email,
          password,
          login,
          referrerLogin: this.referrerLogin,
        },
        succesCode: 201,
        success: (response: AxiosResponse) => {
          this._setToken(response);
          this._clearReferrer();
          this.rootStore.navigation.goToPage(routes.gameKnb);
        },
        errorMessage: 'Registration failed',
      });
    }
  }

  public async checkLogin(login: string) {
    this._checkLogin(login);
  }

  public async login(values: ILoginForm) {
    const { email, password } = values;
    if (email && password) {
      this.rootStore.request({
        method: 'post',
        route: apiRoutes.login,
        params: {
          email,
          password,
        },
        success: (response: AxiosResponse) => {
          this._setToken(response);
          this.rootStore.navigation.goToPage(routes.gameKnb);
        },
        errorMessage: 'Login failed',
      });
    }
  }

  public async forgotPassword(values: IForgotPasswordForm) {
    const { email } = values;
    if (email) {
      this.rootStore.request({
        method: 'post',
        route: apiRoutes.forgotPassword,
        params: {
          email,
        },
        errorMessage: 'Request failed',
      });
    }
  }

  public async resetPassword(values: IResetPasswordForm) {
    const { password, confirmPassword } = values;
    if (this._resetToken && password && password === confirmPassword) {
      this.rootStore.request({
        method: 'post',
        route: apiRoutes.resetPassword,
        params: {
          resetToken: this._resetToken,
          newPassword: password,
        },
        errorMessage: 'Request failed',
      });
    }
  }

  public async confirmEmail(email: string, uuid: string) {
    this.rootStore.request({
      method: 'post',
      route: apiRoutes.confirmEmail,
      params: {
        email,
        uuid,
      },
      errorMessage: 'Request failed',
    });
  }

  public logout = async () => {
    console.log('logout');
    this.rootStore.request({
      method: 'post',
      route: apiRoutes.logout,
      errorMessage: 'Request failed',
    });

    LocalStorageService.clearToken();
    this.rootStore.user.setAuth();
    this.rootStore.navigation.goToPage(routes.home);
  };

  public setResetToken(token?: string) {
    this._resetToken = token;
  }

  public async emailConfirmInit() {
    this.rootStore.request({
      method: 'post',
      route: apiRoutes.emailConfirmInit,
      success: () => {
        runInAction(() => {
          this.isEmailConfirmInit = true;
        });
      },
      errorMessage: 'Init confirm email failed',
    });
  }

  public setReferrer(ref: string) {
    this.referrerLogin = ref;
    LocalStorageService.setReferrer(ref);
  }

  private _checkLogin = debounce(async (login: string) => {
    runInAction(() => {
      this.loginIsValid = undefined;
    });

    await this.rootStore.request({
      method: 'post',
      route: apiRoutes.authCheckLogin,
      params: { login },
      success: () => {
        runInAction(() => {
          this.loginIsValid = true;
        });
      },
      errorMessage: 'Check login failed',
    });
  }, 2000);

  private async _setToken(response: AxiosResponse) {
    if (response.data && response.data.token) {
      LocalStorageService.setToken(response.data.token);

      await this.rootStore.user.self();
      this.rootStore.user.setAuth(true);
    }
  }

  private _clearReferrer() {
    this.referrerLogin = undefined;
    LocalStorageService.clearReferrer();
  }

  private async _loadFromLocalStorage() {
    const token = LocalStorageService.getToken();
    const ref = LocalStorageService.getReferrer();

    if (ref) {
      this.referrerLogin = ref;
    }

    if (token) {
      await this.rootStore.user.self();
    }

    if (token && !!this.rootStore.user.data) {
      this.rootStore.user.setAuth(true);
    }
  }
}

export default AuthStore;
